// Globals
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// Components
import { Drawer } from '@/ui';
import { StyledContentBlock, StyledHeaderBlock, StyledPageTitle, StyledPageWrapper } from './UsersContainerStyles';
import { useForm } from '@/components';
import Tabs from '../../components/Tabs/Tabs';
import AdminsListContainer from '../AdminsListContainer/AdminsListContainer';
import CreateAdminContainer from '../CreateAdminContainer/CreateAdminContainer';
import UsersListContainer from '../UsersListContainer/UsersListContainer';

// Models
import { ROUTES } from '@/models/router.model';

// Modules
import { usePermissions, Resource } from '@/modules/Permissions';

// hooks
import { useModal } from '@/hooks';

// Helpers
import { getUsersTabsConfig, UsersListTabs } from '../../helpers/configs';

const USERS_TAB_RESOURCE: Record<string, Resource> = {
  [UsersListTabs.ADMIN]: Resource.ADMIN_PANEL,
  [UsersListTabs.USERS]: Resource.SITE_USER,
};

const UsersContainer = () => {
  const { t } = useTranslation(['usersList']);
  const { type, id } = useParams<{ type: string; id?: string }>();
  const { canView } = usePermissions();
  const { isOpened: isDrawerOpened, openModal: openDrawer, closeModal: closeDrawer } = useModal();
  const [form] = useForm();
  const navigate = useNavigate();

  const tabsConfig = getUsersTabsConfig(t).filter((tab) => canView(USERS_TAB_RESOURCE[tab.key]));
  const isTypeAllowed = !type || tabsConfig.some((tab) => tab.key === type);

  const onDrawerClose = () => {
    closeDrawer();
    navigate(`${ROUTES.USERS_LIST_INDEX}/${type}`);
  };

  const onCreateUser = () => {
    navigate(`${ROUTES.USERS_LIST_INDEX}/${type}/create`);
  };

  const getPageContent = () => {
    switch (type) {
      case UsersListTabs.ADMIN:
        return canView(Resource.ADMIN_PANEL) ? <AdminsListContainer onCreateUser={onCreateUser} /> : null;
      case UsersListTabs.USERS:
        return canView(Resource.SITE_USER) ? <UsersListContainer /> : null;
    }
  };

  useEffect(() => {
    if (id) {
      openDrawer();
    } else {
      closeDrawer();
      form.resetFields();
    }
  }, [id]);

  useEffect(() => {
    if (!isTypeAllowed) {
      const fallback = tabsConfig.at(0)?.key;

      if (fallback) {
        navigate(`${ROUTES.USERS_LIST_INDEX}/${fallback}`, { replace: true });
      }
    }
  }, [isTypeAllowed]);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('users_list_users')}</StyledPageTitle>
      </StyledHeaderBlock>
      <Tabs config={tabsConfig} />
      <StyledContentBlock>{getPageContent()}</StyledContentBlock>
      <Drawer open={isDrawerOpened} onClose={onDrawerClose}>
        <CreateAdminContainer id={id} form={form} />
      </Drawer>
    </StyledPageWrapper>
  );
};

export default UsersContainer;
